﻿<template>
  <IonModal
    ref="modal"
    :is-open="isOpen"
    @will-dismiss="onWillDismiss"
    :initial-breakpoint="0.5"
    :breakpoints="[0, 0.25, 0.5, 0.75]"
    class="full-width-modal"
  >
    <IonHeader>
      <IonToolbar>
        <IonTitle>{{ $t('nav.userMenu') }}</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="closeModal">
            <IonIcon :icon="closeOutline" />
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent class="modal-content">
      <!-- User info section - always show for debugging -->
      <div class="user-info-section">
        <div class="user-info" @click="goToProfile" :class="{ clickable: isAuthenticated }">
          <IonAvatar class="modal-avatar">
            <img :src="avatarUrl" :alt="$t('nav.userAvatar')" />
          </IonAvatar>
          <div class="user-details">
            <div class="user-name">{{ userName || 'Guest User' }}</div>
            <div class="user-email">{{ userEmail || 'No email' }}</div>
          </div>
        </div>
        <div class="divider"></div>
      </div>

      <IonList class="settings-list">
        <IonItem button @click="showLanguageOptions = !showLanguageOptions">
          <IonIcon :icon="languageOutline" slot="start" />
          <IonLabel>
            <h3>{{ $t('common.language') }}</h3>
            <p>{{ getLocaleName(currentLocale) }}</p>
          </IonLabel>
          <IonIcon :icon="showLanguageOptions ? chevronUpOutline : chevronDownOutline" slot="end" />
        </IonItem>

        <div v-if="showLanguageOptions" class="language-options">
          <IonItem
            v-for="locale in availableLocales"
            :key="locale.code"
            button
            @click="handleLanguageChange(locale.code)"
            class="language-option"
            :class="{ selected: locale.code === currentLocale }"
          >
            <div class="language-item">
              <span class="language-flag">{{ locale.flag }}</span>
              <span class="language-name">{{ locale.name }}</span>
              <IonIcon
                v-if="locale.code === currentLocale"
                :icon="checkmarkOutline"
                class="selected-icon"
              />
            </div>
          </IonItem>
        </div>

        <IonItem class="theme-switch-container">
          <ThemeUnifiedSwitch
            mode="style"
            size="10"
            @theme-toggled="handleStyleModeToggleWrapper"
          />
        </IonItem>

        <!-- Mockup Mode Toggle (Development only) -->
        <IonItem v-if="isDevelopment">
          <IonIcon
            :icon="isMockupMode ? flaskOutline : flask"
            slot="start"
            :color="isMockupMode ? 'primary' : 'medium'"
          />
          <IonLabel>
            <h3>{{ $t('common.mockupMode') }}</h3>
            <p>
              {{ isMockupMode ? $t('common.enabled') : $t('common.disabled') }}
            </p>
          </IonLabel>
          <DarkLightSwitch
            :size="12"
            :model-value="isMockupMode"
            @update:model-value="handleMockupToggle"
          />
        </IonItem>

        <IonItem button @click="goToSettings">
          <IonIcon :icon="settingsOutline" slot="start" />

          <IonLabel>
            <h3>{{ $t('nav.settings') }}</h3>
            <p>{{ $t('nav.moreSettings') }}</p>
          </IonLabel>
          <IonIcon :icon="chevronForwardOutline" slot="end" />
        </IonItem>

        <IonItem v-if="isAuthenticated" button @click="handleLogout" class="logout-item">
          <IonIcon :icon="logOutOutline" slot="start" />
          <IonLabel>
            <h3>{{ $t('common.logout') }}</h3>
          </IonLabel>
        </IonItem>

        <IonItem v-else button @click="handleLogin" class="login-item">
          <IonIcon :icon="logInOutline" slot="start" />
          <IonLabel>
            <h3>{{ $t('common.login') }}</h3>
          </IonLabel>
        </IonItem>
      </IonList>
    </IonContent>
  </IonModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
} from '@ionic/vue';
import {
  languageOutline,
  settingsOutline,
  logOutOutline,
  logInOutline,
  chevronDownOutline,
  chevronUpOutline,
  chevronForwardOutline,
  checkmarkOutline,
  closeOutline,
  flask,
  flaskOutline,
} from 'ionicons/icons';
import { useRouter } from 'vue-router';
import { useUser } from '@/common/composables/useUser';
import { useUserStore } from '@/common/stores/userStore';
import { useLocale } from '@/common/composables/useLocale';
import { useTheming } from '@/common/composables/useTheming';
import { useProfileStore } from '@/features/profile/stores/profileStore';
import { authService } from '@/features/common/auth/services/authService';
import { notificationService } from '@/common/services/notification/notificationService';
import DarkLightSwitch from '@/common/components/ui/switch/DarkLightSwitch.vue';
import { useMockupMode } from '@/common/services/mockup';
import ThemeUnifiedSwitch from '@/common/components/ui/switch/ThemeUnifiedSwitch.vue';
import { logger } from '@/common/services/config';
const { themeMode, toggleStyleMode } = useTheming();

// Props (kept for backward compatibility but not used)
interface Props {
  isOpen: boolean;
  currentTheme?: string;
}

defineProps<Props>();

// Emits
const emit = defineEmits<{
  close: [];
  themeToggled: [];
}>();

// Composables
const router = useRouter();
const { isAuthenticated, userName, userEmail } = useUser();
const { currentLocale, availableLocales, changeLocale, getLocaleName, t } = useLocale();
const profileStore = useProfileStore();
const { getUser } = useUserStore();

// Mockup mode composable
const { isMockupMode, toggleMockupMode } = useMockupMode();

const isDevelopment = computed(() => import.meta.env.VITE_APP_MODE === 'development');

// Reactive state
const modal = ref();
const showLanguageOptions = ref(false);

// Computed
const avatarUrl = computed(() => {
  // Use the user's photo from the profile store if available
  if (profileStore.currentProfile?.photo) {
    return profileStore.currentProfile.photo;
  }
  // Fallback to default avatar
  return 'https://ionicframework.com/docs/img/demos/avatar.svg';
});

// Methods
const closeModal = () => {
  modal.value?.$el.dismiss();
};

const onWillDismiss = () => {
  showLanguageOptions.value = false;
  emit('close');
};

const handleLanguageChange = (localeCode: string) => {
  changeLocale(localeCode);
  notificationService.info('notifications.languageChanged', {
    language: getLocaleName(localeCode),
  });
  closeModal();
};

const handleMockupToggle = () => {
  toggleMockupMode();
  notificationService.info('notifications.mockupModeChanged', {
    mode: isMockupMode.value ? t('common.enabled') : t('common.disabled'),
  });
};

const goToSettings = () => {
  router.push({ name: 'Settings' });
  closeModal();
};

const goToProfile = () => {
  router.push({ name: 'MyProfile' });
  closeModal();
};

const handleLogin = async () => {
  closeModal();
  try {
    await authService.signIn();
  } catch (error) {
    logger.error('Login failed:', error);
  }
};

const handleLogout = async () => {
  closeModal();
  try {
    await authService.signOut();
    notificationService.success('notifications.logoutSuccess');
  } catch (error) {
    logger.error('Logout failed:', error);
  }
};
const handleStyleModeToggle = (isClassic: boolean) => {
  toggleStyleMode();
  emit('themeToggled');

  const newStyle = isClassic ? 'Classique' : 'Moderne';
  notificationService.info('notifications.themeChanged', {
    theme: `Style ${newStyle}`,
  });
};

const handleStyleModeToggleWrapper = () => {
  handleStyleModeToggle(themeMode.value === 'modern');
};

// Load profile on mount if authenticated
onMounted(async () => {
  if (isAuthenticated.value && !profileStore.currentProfile) {
    try {
      await getUser();
      await profileStore.fetchCurrentProfile();
    } catch (error) {
      logger.error('Failed to load user profile:', error);
    }
  }
});
</script>

<style scoped>
/* Full width modal styles */
:deep(.full-width-modal) {
  --width: 100% !important;
  --max-width: 100% !important;
  --min-width: 100% !important;
}

:deep(.full-width-modal .modal-wrapper) {
  width: 100% !important;
  max-width: 100% !important;
}

.modal-content {
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: 0;
  --padding-bottom: 0;
  --background: var(--ion-background-color, #ffffff);
  min-height: 800px;
  width: 100%;
}

.user-info-section {
  padding: 24px 16px 16px 16px;
  background: var(--ion-color-light, #f4f5f8);
  margin: 16px;
  border-radius: 16px;
  transition: background-color 0.3s ease;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  transition: all 0.2s ease;
}

.user-info.clickable {
  cursor: pointer;
  border-radius: 12px;
  padding: 8px;
  margin: -8px -8px 8px -8px;
}

.user-info.clickable:hover {
  background: var(--ion-color-light);
  transform: translateY(-1px);
}

.modal-avatar {
  width: 56px;
  height: 56px;
  transition: transform 0.2s ease;
}

.modal-avatar:hover {
  transform: scale(1.05);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 600;
  font-size: 18px;
  color: var(--ion-text-color, var(--ion-color-light));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.user-email {
  font-size: 14px;
  color: var(--ion-color-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.divider {
  width: 100%;
  height: 1px;
  background-color: var(--ion-color-light);
  margin: 0;
  transition: background-color 0.3s ease;
}

.settings-list {
  padding: 8px 0 24px 0;
}

.settings-list ion-item {
  --padding-start: 16px;
  --padding-end: 16px;
  cursor: pointer;
  --min-height: 56px;
}

.settings-list ion-item:hover {
  --background: var(--ion-color-light);
  transition: background-color 0.2s ease;
}

.language-options {
  background-color: var(--ion-color-light);
  margin: 0 8px 8px 8px;
  border-radius: 12px;
  overflow: hidden;
  transition: background-color 0.3s ease;
  border: 1px solid transparent;
}

.language-option {
  --padding-start: 24px;
  --padding-end: 16px;
  --min-height: 48px;
}

.language-option.selected {
  --background: var(--ion-background-color-secondary);
  --color: var(--ion-color-primary);
}

.language-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.language-flag {
  font-size: 20px;
}

.language-name {
  flex: 1;
  font-weight: 500;
  font-size: 16px;
}

.theme-switch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.selected-icon {
  color: var(--ion-color-primary);
  font-size: 20px;
}

.logout-item {
  --color: var(--ion-color-danger);
}

.logout-item ion-icon {
  color: var(--ion-color-danger);
}

.login-item {
  --color: var(--ion-color-primary);
}

.login-item ion-icon {
  color: var(--ion-color-primary);
}

/* Larger text and spacing for mobile */
ion-label h3 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 2px;
}

ion-label p {
  font-size: 14px;
}

/* Dark mode adjustments */
body.dark .modal-content {
  --background: var(--ion-background-color-primary);
}

body.dark .user-info-section {
  background: var(--ion-background-color-secondary);
  border-radius: 16px;
  margin: 16px;
  padding: 24px 16px 16px 16px;
}

body.dark .user-info.clickable:hover {
  background: var(--ion-background-color-tertiary);
}

body.dark .user-name {
  color: var(--ion-text-color);
}

body.dark .user-email {
  color: var(--ion-color-primary);
}

body.dark .divider {
  background-color: var(--ion-background-color-tertiary);
}

body.dark .settings-list {
  background: var(--ion-background-color-primary);
}

body.dark .settings-list ion-item {
  --background: var(--ion-background-color-primary);
  --color: var(--ion-text-color);
}

body.dark .settings-list ion-item:hover {
  --background: var(--ion-background-color-tertiary);
}

body.dark .language-options {
  background-color: var(--ion-background-color-secondary);
  border: 1px solid var(--ion-background-color-tertiary);
}

body.dark .language-option {
  --background: var(--ion-background-color-secondary);
  --color: var(--ion-text-color);
}

body.dark .language-option:hover {
  --background: var(--ion-background-color-tertiary);
}

body.dark .language-option.selected {
  --background: var(--ion-background-color-secondary);
  --color: var(--ion-background-color-primary);
}

body.dark .language-name {
  color: var(--ion-text-color);
}

body.dark .selected-icon {
  color: var(--ion-background-color-primary);
}

body.dark ion-label h3 {
  color: var(--ion-text-color);
}

body.dark ion-label p {
  color: var(--ion-color-primary);
}

body.dark ion-icon {
  color: var(--ion-icon-primary);
}

body.dark .logout-item {
  --color: var(--ion-color-danger);
}

body.dark .logout-item ion-icon {
  color: var(--ion-color-danger);
}

body.dark .login-item {
  --color: var(--ion-color-primary);
}

body.dark .login-item ion-icon {
  color: var(--ion-color-primary);
}

/* Toggle styling to keep the dot blue */
ion-toggle {
  --color: var(--ion-color-primary);
  --color-checked: var(--ion-color-primary);
  --handle-background: var(--ion-color-primary);
  --handle-background-checked: var(--ion-color-primary);
}

body.dark ion-toggle {
  --color: var(--ion-color-primary);
  --color-checked: var(--ion-color-primary);
  --handle-background: var(--ion-color-primary);
  --handle-background-checked: var(--ion-color-primary);
}

/* Dark mode media query for additional enhancements */
@media (prefers-color-scheme: dark) {
  .modal-content {
    box-shadow: var(--ion-box-shadow-hover);
  }

  .language-options {
    box-shadow: var(--ion-box-shadow-hover);
  }

  .settings-list ion-item:hover {
    box-shadow: var(--ion-box-shadow);
  }
}
</style>

<!-- Global styles for modal width -->
<style>
.full-width-modal {
  --width: 100% !important;
  --max-width: 100% !important;
  --min-width: 100% !important;
}

.full-width-modal .modal-wrapper {
  width: 100% !important;
  max-width: 100% !important;
}

.full-width-modal ion-backdrop {
  --backdrop-opacity: 0.4;
}
</style>
